import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Shield, X, Copy, Trash2 } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const CAP_LABELS = {
  view_messages: "Messages",
  view_customers: "Customers",
  view_financials: "Financials & Earnings",
  edit_services: "Edit services",
  manage_schedule: "Schedule",
  manage_payouts: "Payouts",
  view_reports: "Reports",
  manage_team: "Team management",
};

export default function TeamPage() {
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: "", team_role: "service_provider", permission_overrides: {} });
  const [lastInvite, setLastInvite] = useState(null);
  const [editing, setEditing] = useState(null);
  useDocumentTitle("Team");

  const load = async () => {
    const [m, i] = await Promise.all([api.get("/team/members"), api.get("/team/invitations")]);
    setMembers(m.data || []);
    setInvitations(i.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.email.trim()) { toast.error("Email required"); return; }
    try {
      const { data } = await api.post("/team/invitations", form);
      toast.success("Invitation created — share the accept link below.");
      setLastInvite(data);
      setForm({ email: "", team_role: "service_provider", permission_overrides: {} });
      setShowInvite(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };

  const revoke = async (id) => {
    try { await api.post(`/team/invitations/${id}/revoke`); load(); toast.success("Revoked"); }
    catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };

  const updateMember = async () => {
    if (!editing) return;
    try {
      await api.put(`/team/members/${editing.id}`, { team_role: editing.team_role, permission_overrides: editing.permission_overrides, status: editing.status });
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };

  return (
    <DashboardShell>
      <div className="max-w-5xl" data-testid="team-page">
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Provider · Team</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Team & agency members</h1>
            <p className="text-[#252A34]/70 text-sm mt-1">Invite team members, assign roles, and control what each can see.</p>
          </div>
          <button onClick={() => setShowInvite(true)} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="team-invite-btn">
            <Plus className="h-4 w-4" /> Invite member
          </button>
        </div>

        {lastInvite?.dev_accept_url && (
          <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="team-invite-link">
            <div className="font-medium text-emerald-800">Share this accept link with {lastInvite.email} (dev mode — real email later):</div>
            <div className="mt-2 flex items-center gap-2">
              <input readOnly value={`${window.location.origin}${lastInvite.dev_accept_url}&e=${encodeURIComponent(lastInvite.email)}`} className="flex-1 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-mono" data-testid="team-invite-url" />
              <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}${lastInvite.dev_accept_url}&e=${encodeURIComponent(lastInvite.email)}`); toast.success("Copied"); }} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-2 text-xs" data-testid="team-invite-copy">
                <Copy className="h-3 w-3" /> Copy
              </button>
            </div>
          </div>
        )}

        <section className="mb-8">
          <h2 className="font-display text-lg font-semibold text-[#071b22] mb-3">Members ({members.length})</h2>
          {members.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
              <Users className="h-6 w-6 mx-auto text-[#165a71]" />
              <div className="font-display text-lg text-[#071b22] font-semibold mt-2">No team yet.</div>
              <div className="text-sm text-[#252A34]/70 mt-1">Invite your first team member.</div>
            </div>
          ) : (
            <ul className="rounded-2xl border border-slate-200 bg-white divide-y" data-testid="team-members-list">
              {members.map((m) => (
                <li key={m.id} className="p-4 flex items-center gap-3" data-testid={`team-member-${m.id}`}>
                  <div className="h-10 w-10 rounded-xl bg-[#165a71]/10 text-[#165a71] flex items-center justify-center font-semibold">{(m.display_name || m.email).slice(0,2).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-[#071b22] truncate">{m.display_name || m.email}</div>
                    <div className="text-xs text-[#252A34]/60">{m.email} · <b>{m.team_role}</b> · {m.status}</div>
                  </div>
                  <button onClick={() => setEditing({ ...m })} className="text-xs font-medium text-[#165a71]" data-testid={`team-member-edit-${m.id}`}>Manage</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {invitations.filter((i) => i.status === "pending").length > 0 && (
          <section>
            <h2 className="font-display text-lg font-semibold text-[#071b22] mb-3">Pending invitations</h2>
            <ul className="rounded-2xl border border-slate-200 bg-white divide-y">
              {invitations.filter((i) => i.status === "pending").map((i) => (
                <li key={i.id} className="p-4 flex items-center gap-3 text-sm" data-testid={`invite-row-${i.id}`}>
                  <Shield className="h-4 w-4 text-[#165a71]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#071b22] truncate">{i.email}</div>
                    <div className="text-xs text-[#252A34]/60">{i.team_role} · invited {new Date(i.created_at).toLocaleDateString()}</div>
                  </div>
                  <button onClick={() => revoke(i.id)} className="text-xs text-rose-600" data-testid={`invite-revoke-${i.id}`}>Revoke</button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {showInvite && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071b22]/50 p-4">
            <div className="w-full max-w-lg rounded-3xl bg-white p-6 border border-slate-200" data-testid="team-invite-form">
              <div className="flex items-start justify-between">
                <div className="font-display text-lg font-semibold text-[#071b22]">Invite team member</div>
                <button onClick={() => setShowInvite(false)} className="rounded p-1 hover:bg-slate-100"><X className="h-4 w-4" /></button>
              </div>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-xs text-[#252A34]/70">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="invite-email" />
                </div>
                <div>
                  <label className="text-xs text-[#252A34]/70">Role</label>
                  <select value={form.team_role} onChange={(e) => setForm((p) => ({ ...p, team_role: e.target.value }))} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="invite-role">
                    {["owner", "administrator", "manager", "scheduler", "service_provider", "freelancer", "customer_support", "finance", "marketing"].map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-xs text-[#252A34]/70 mb-1">Permission overrides (optional)</div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    {Object.entries(CAP_LABELS).map(([k, label]) => (
                      <label key={k} className="inline-flex items-center gap-1">
                        <input type="checkbox" checked={!!form.permission_overrides[k]} onChange={(e) => setForm((p) => ({ ...p, permission_overrides: { ...p.permission_overrides, [k]: e.target.checked } }))} data-testid={`invite-perm-${k}`} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setShowInvite(false)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                <button onClick={submit} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="invite-submit">Send invite</button>
              </div>
            </div>
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071b22]/50 p-4">
            <div className="w-full max-w-lg rounded-3xl bg-white p-6 border border-slate-200" data-testid="team-member-form">
              <div className="flex items-start justify-between">
                <div className="font-display text-lg font-semibold text-[#071b22]">Manage {editing.display_name || editing.email}</div>
                <button onClick={() => setEditing(null)} className="rounded p-1 hover:bg-slate-100"><X className="h-4 w-4" /></button>
              </div>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-xs text-[#252A34]/70">Role</label>
                  <select value={editing.team_role} onChange={(e) => setEditing((p) => ({ ...p, team_role: e.target.value }))} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="tm-role">
                    {["owner", "administrator", "manager", "scheduler", "service_provider", "freelancer", "customer_support", "finance", "marketing"].map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#252A34]/70">Status</label>
                  <select value={editing.status} onChange={(e) => setEditing((p) => ({ ...p, status: e.target.value }))} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="tm-status">
                    {["active", "suspended", "removed"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-xs text-[#252A34]/70 mb-1">Permission overrides</div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    {Object.entries(CAP_LABELS).map(([k, label]) => {
                      const cur = editing.permission_overrides?.[k];
                      return (
                        <label key={k} className="inline-flex items-center gap-1">
                          <input type="checkbox" checked={cur === true} onChange={(e) => setEditing((p) => ({ ...p, permission_overrides: { ...p.permission_overrides, [k]: e.target.checked } }))} data-testid={`tm-perm-${k}`} />
                          {label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setEditing(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                <button onClick={updateMember} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="tm-save">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
